import InputHandler from "../input/InputHandler";
import SkillTreeInputHandler from "../input/SkillTreeInputHandler";
import Character from "../model/character/Character";
import Crew from "../model/crew/Crew";
import Grid from "../model/grid/Grid";
import GridInversionOfControl from "../model/grid/GridInversionOfControl";
import WorldGrid from "../model/grid/WorldGrid";
import LightingCalculator from "../model/lighting/LightingCalculator";
import Vector from "../model/math/Vector";
import GameOptions from "../model/options/GameOptions";
import PerformanceMetrics from "../model/performance/PerformanceMetrics";
import { Settings } from "../Settings";
import State from "../State";
import NearbyTileUtil from "../util/NearbyTileUtil";

export default class FrameLogic {
    /**
     * 
     * @param {InputHandler} inputHandler 
     * @param {SkillTreeInputHandler} skillTreeInputHandler
     */
    constructor(inputHandler, skillTreeInputHandler) {
        /**
         * @constant
         * @type {InputHandler}
         */
        this.inputHandler = inputHandler;
        /**
         * @constant
         * @type {SkillTreeInputHandler}
         */
        this.skillTreeInputHandler = skillTreeInputHandler;

        /**
         * @private
         * @type {number}
         */
        this.elapsedTurnFrames = 0;

        /**
         * @private
         * @type {number}
         */
        this.prevWorldGridShiftCount = -1;

        /**
         * This exists so that the performFrameActionsForGrid function can access the current frame time ratio
         * @private
         * @type {number}
         */
        this.frameTimeRatio = 1;
        /**
         * This is here so for performFrameActionsForGrid.
         * @private
         * @type {boolean}
         */
        this.turnStart = false;

        /**
         * @private
         * @type {number}
         */
        this.lastSaveTime = Date.now();

        /**
         * @private
         * @constant
         * @type {Vector}
         */
        this.workingGridCenter = new Vector(0, 0);
        
        /**
         * @private
         * @type {number}
         */
        this.elapsedSaveThreshold = 1000 * 60 * 2; // Save every 2 minutes?

        const thisRef = this;

        /**
         * @type {Function}
         */
        this.performMonsterFrameActionsForGrid = 
            /**
             * @param {Grid} grid 
             */
            function(grid) {
                const characters = grid.characters;
                for (let i = 0; i < characters.length; i++) {
                    if (characters[i].isMonster()) {
                        characters[i].performFrameActions(thisRef.frameTimeRatio, thisRef.turnStart);
                    }
                }
            };

        /**
         * @private
         * @type {Function}
         */
        this.processMonsterTurnsForGrid = 
            /**
             * @param {Grid} grid 
             */
            function(grid) {
                const characters = grid.characters;
                for (let i = 0; i < characters.length; i++) {
                    const character = characters[i];
                    const tile = character.position.tile;
                    if (character.isMonster() && tile && tile.lighting.everVisibleToCharacter) {
                        character.chooseTurn();
                    }
                }
            };
        
    }

    /**
     * @param {State} state 
     * @param {number} frameTimeRatio 
     */
    processFrameLogic(state, frameTimeRatio) {
        
        // Update the frame number
        /////state.gameTime.frameNumber++;
        state.gameTime.frameNumber += frameTimeRatio;
        if (state.gameTime.frameNumber > 1e10) {
            state.gameTime.frameNumber = 0; // no need for the frame number to become crazy big
        }

        this.inputHandler.handleInput();
        this.skillTreeInputHandler.handleInput();

        let newTurn = false;
        this.elapsedTurnFrames += frameTimeRatio;
        if (this.elapsedTurnFrames >= Settings.time.turnFrames) {
            newTurn = true;
            this.elapsedTurnFrames -= Settings.time.turnFrames;
        }

        // Check to see if the world grid has shifted 
        const worldGridShiftCount = state.worldGrid.shiftCount;
        if (this.prevWorldGridShiftCount !== worldGridShiftCount) {
            this.prevWorldGridShiftCount = worldGridShiftCount;
        }

        if (newTurn) {
            this.processTurnLogic(state);
        }

        // Have all the nearby characters perform their actions
        this.performCharacterActions(state, frameTimeRatio, newTurn);

        // See if we have unlocked the next layer.
        if (state.partyTarget.nextLayerDescription && state.partyTarget.nextLayerDescription.minDepth <= state.runStatistics.maxDepth) {
            state.partyTarget.unlockNextLayer();
        }

        state.physicsManager.updatePhysicsForFrame(frameTimeRatio);

        //////state.actionManager.processActionsForFrame(frameTimeRatio); // <-- Combat stuff.  Attack Actions
        state.effectManager.updateEffectsForFrame(frameTimeRatio);


        // We want the canvas centered on the character if he is moving 
        this.updateGridCenter(state, frameTimeRatio);
        
        // Initialize the projection for this frame.
        state.projection.updateProjectionForFrame();

        // Load/unload terrain based on what part of the world is visible.
        state.gridLoadingManager.manageLoadedGridsForFrame();


        ///////state.lightingCalculator.updateForFrame(frameTimeRatio);
        if (newTurn) {
            this.updateDynamicLighting(state.lightingCalculator, state.crew, state.gameOptions);

            state.skillTreeGraph.updateSkillsForTurn();
        }

        state.skillTreeGraph.updateSkillsForFrame(frameTimeRatio);

        state.sprites.spriteAnimationManager.updateForFrame(frameTimeRatio);

        state.infoTextProcessor.updateTextForFrame(frameTimeRatio);

        const currentTime = Date.now();
        if (currentTime - this.lastSaveTime > this.elapsedSaveThreshold) {
            state.saveManager.saveGame(state);
            this.lastSaveTime = currentTime;
        }
    }

    /**
     * @param {LightingCalculator} lightingCalculator
     * @param {Crew} crew
     * @param {GameOptions} gameOptions
     */
    updateDynamicLighting(lightingCalculator, crew, gameOptions) {        

        PerformanceMetrics.startTime('LightingReset');
        lightingCalculator.resetForFrame();
        PerformanceMetrics.endTime('LightingReset');

        const miners = crew.miners;
        PerformanceMetrics.startTime('DynamicLighting');
        for (let i = 0; i < miners.length; i++) {
            const tile = miners[i].position.tile;
            if (!tile) {
                continue;
            }

            if (i === 0) {
                lightingCalculator.calculatePrimaryPositionalLighting(tile);
            }
            else {
                lightingCalculator.calculateSecondaryPositionalLighting(tile);
            }
        }
        PerformanceMetrics.endTime('DynamicLighting');
    }

    /**
     * @private
     * @param {State} state 
     * @param {number} frameTimeRatio 
     */
    updateGridCenter(state, frameTimeRatio) {

        // If the player pans away from the crew and then just leaves it like that, the crew will
        // mine themselves far away and go off the edge of the world. 
        // Exit pan mode after 10 seconds of no input.
        if (!state.panAnimation.animating && !state.projection.stayCenteredOnCharacter && Date.now() - this.inputHandler.mapPannedTime > (1000 * 10)) {
            //console.log('Pan animation due to timeout!');
            state.panAnimation.startAnimation();
        }

        // If pan animation is active, then let it control the projection center.
        if (state.panAnimation.animating) {
            state.panAnimation.updateForFrame(frameTimeRatio);
            return;
        }

        // If the player is panning freely, then don't update anything.
        if (!state.projection.stayCenteredOnCharacter) {
            return;
        }

        // this.workingGridCenter
        state.crew.calculateCrewCenterCoordinate(this.workingGridCenter);
        state.projection.setGridCenter(this.workingGridCenter.x + Settings.tile.halfSize, this.workingGridCenter.y + Settings.tile.halfSize);

        ///////////////////////////////////////////////////

        // // Keep the character at the center of the screen.
        // const worldCoordinate = state.crew.main.position.worldCoordinateCenter;
        // state.projection.setGridCenter(worldCoordinate.x, worldCoordinate.y);
    }

    /**
     * @private
     * @param {State} state 
     * @param {number} frameTimeRatio 
     * @param {boolean} turnStart
     */
    performCharacterActions(state, frameTimeRatio, turnStart) {

        const miners = state.crew.miners;
        for (let i = 0; i < miners.length; i++) {
            const miner = miners[i];
            if (!miner.position.tile) {
                continue;
            }

            miner.performFrameActions(frameTimeRatio, turnStart);
        }

        this.frameTimeRatio = frameTimeRatio; // make a class member reference to the current ratio
        this.turnStart = turnStart;

        // Let the visible monsters do stuff.
        GridInversionOfControl.allVisibleGrids(state.worldGrid, state.projection, this.performMonsterFrameActionsForGrid);
    }

    /**
     * @private
     * @param {WorldGrid} worldGrid
     * @param {Crew} crew
     * @returns {boolean} True if the character has a tile in the loaded world.
     */
    validateCrewTiles(worldGrid, crew) {

        const centerTile = crew.calculateCrewCenterTile(); // <-- will be null if the party center is not on an active grid
        if (!centerTile) {
            // The party is off the grid.
            return false;
        }

        const miners = crew.miners;

        for (let i = 0; i < miners.length; i++) {
            const miner = miners[i];
            const minerValid = this.validateCharacterTile(worldGrid,  miner);

            // If the main miner is in a valid grid but the secondary miner is not, then move the
            // secondary miner near the main miner.
            if (!minerValid) {
                const nearbyTile = NearbyTileUtil.findAvailableNearbyTile(miner, crew, centerTile); // find near crew center
                if (nearbyTile) {
                    console.log('FrameLogic.validateCrewTiles() moving miner to valid grid near crew center.');
                    miner.position.setTileAndOriginPosition(nearbyTile, nearbyTile.origin.x, nearbyTile.origin.y);
                }
                else {
                    console.log('FrameLogic.validateCrewTiles() failed to move miner near crew center');
                }
            }
        }

        return true;
    }

    /**
     * @private
     * @param {WorldGrid} worldGrid 
     * @param {Character} character
     * @returns {boolean} True if the character has a tile in the loaded world.
     */
    validateCharacterTile(worldGrid, character) {

        // Possibilities:
        // 1. character has tile
        // 1a. tile is in loaded grid - nothing to do.
        // 1b. tile is in unloaded grid - nullify, reset stuff

        // 2. character does not have a tile
        // 2a. character is in unloaded grid - nothing to do. not an error.
        // 2b. character is in loaded grid - set tile and reset stuff.

        // Find the character tile based on their position.
        const characterTile = worldGrid.findGridTile(character.position.worldCoordinateOrigin);

        if (character.position.tile) {
            if (characterTile && characterTile !== character.position.tile) {
                character.onInvalidGrid();
                character.position.setTileAndOriginPosition(characterTile, characterTile.origin.x, characterTile.origin.y);

                return true;
            }
            else if (!characterTile) {
                character.onInvalidGrid();
                return false;
            }

            return true;
        }
        else {
            if (characterTile) {
                character.onInvalidGrid();
                character.position.setTileAndOriginPosition(characterTile, characterTile.origin.x, characterTile.origin.y);
                
                return true;
            }

            return false;
        }
    }

    /**
     * @private
     * @param {State} state 
     */
    processTurnLogic(state) {
        state.gameTime.turnNumber++;
        state.statisticsIncrementor.incrementTurns(1);

        // Make sure the crew tile is OK
        if (!this.validateCrewTiles(state.worldGrid, state.crew)) {
            state.vectorField.reset();
            return;
        }

        //////////////////////////////////////////
        // Bonus Collection
        //////////////////////////////////////////

        //state.bonusCollection.updateForTurn();
 
        //////////////////////////////////////////
        // Automation
        //////////////////////////////////////////

        state.automationManager.updateForTurn();

        //////////////////////////////////////////
        // Vector Field
        //////////////////////////////////////////

        state.mineControl.updateForTurn();

        //////////////////////////////////////////
        // Turn selection
        //////////////////////////////////////////
        
        // Assign tiles to miners
        state.crewBrain.updateForTurn();

        GridInversionOfControl.allVisibleGrids(state.worldGrid, state.projection, this.processMonsterTurnsForGrid);
    }
}